import {Button, Card, Flex, Input, Space} from "antd";
import Title from "antd/es/typography/Title";
import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp, faArrowUpWideShort, faRotate, faTrash} from "@fortawesome/free-solid-svg-icons";
import {generateScenePrompt, generateScenesPrompt, improveScenePrompt} from "./prompts/PlotScenesPrompts";
import {aiChainPrompt, similaritySearch} from "../../../../../../ai/Prompt";
import {DraftScene, DraftSection} from "../../../../../../feature/Draft";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {updateScene, updateSection} from "../../../../../../feature/project/draftSlice";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";

export const PlotSectionDetails = (props: { section: DraftSection }) => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)

    const generateScenes = async (model: string, projectId: string) => {
        let sections = draft.story.acts.flatMap((act) => act.chapters.flatMap((chapter) => chapter.sections))
        let sectionIndex = sections.findIndex((item) => item.id === props.section.id) ?? -2
        let prevSection = sections[sectionIndex - 1]
        let nextSection = sections[sectionIndex + 1]
        let context = await similaritySearch({
            project: projectId,
            query: props.section.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateScenesPrompt({
                section: props.section,
                prevSection: prevSection,
                nextSection: nextSection,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                prevScenes: [],
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateSection({section: {...props.section, scenes: data.data}}))
    }

    const improveScene = async (model: string, scene: DraftScene, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.section.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: improveScenePrompt({
                scene: scene,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                scenes: props.section.scenes,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateScene({
            scene: {
                ...scene,
                title: data.data.title,
                description: data.data.description ?? "",
                shots: []
            }
        }))
    }

    const generateScene = async (model: string, scene: DraftScene, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.section.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateScenePrompt({
                scene: scene.id,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                scenes: props.section.scenes,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateScene({
            scene: {
                ...scene,
                title: data.data.title,
                description: data.data.description ?? "",
                shots: []
            }
        }))
    }

    return (
        <Flex style={{padding: 4, flexGrow: 1, height: "100%", overflowY: 'auto', scrollbarWidth: "none"}}
              vertical={true} gap={10}>
            <Card title={props.section.title}>
                <TextAreaActions
                    style={{border: "none"}}
                    value={props.section.description}
                    autoSize={true}
                    validate={text => true}
                    onChange={(text: string) => {
                        dispatch(updateScene({
                            // @ts-ignore
                            scene: {
                                ...props.section,
                                description: text
                            }
                        }))
                    }}
                />
            </Card>
            <Card>
                <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                    <Title level={5} style={{margin: 0}}>Scenes</Title>
                    <Space>
                        <GenButton
                            style={{width: 130}}
                            type={'primary'}
                            actions={[{
                                key: "1", title: "Generate", action: async (model: string, projectId: string) =>
                                    generateScenes(model, projectId)
                                        .catch((e) => {
                                            console.log(e)
                                        })
                            }]
                            }/>
                    </Space>
                </Flex>
            </Card>

            <Space direction={'vertical'}>
                {(props.section.scenes ?? []).map((item, index) =>
                    <Card style={{flexGrow: 1}} key={index}
                          title={<Flex justify={'space-between'}>
                              <Flex gap={10} style={{flexGrow: 1}}>
                                  <div style={{
                                      height: 30,
                                      width: 30,
                                      borderRadius: 30,
                                      background: "#ffffff",
                                      color: "#000",
                                      minWidth: 30,
                                      textAlign: "center",
                                      padding: 3
                                  }}>{index + 1}</div>
                                  <DebounceInput
                                      style={{fontWeight: 'bold'}}
                                      placeholder={"Scene title"}
                                      value={item.title}
                                      onChange={(text) => {
                                          dispatch(updateScene({
                                              // @ts-ignore
                                              scene: {
                                                  ...item,
                                                  title: text
                                              }
                                          }))
                                      }}/>
                                  <Space>
                                      <Button disabled={index == 0}
                                              style={{color: index == 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              type={'text'}
                                              icon={<FontAwesomeIcon icon={faArrowUp}/>}
                                              onClick={() => {
                                                  let scenes = [...props.section.scenes]
                                                  let prev = {...scenes[index - 1]}
                                                  let current = {...scenes[index]}
                                                  let prevId = prev.id
                                                  prev.id = current.id
                                                  current.id = prevId

                                                  dispatch(updateSection({
                                                      // @ts-ignore
                                                      section: {
                                                          ...props.section,
                                                          scenes: [...scenes.filter((item) => item.id !== prev.id && item.id !== current.id)].concat([prev, current]).sort((a, b) => a.id - b.id) as []
                                                      }
                                                  }))
                                              }}/>
                                      <Button disabled={index == props.section.scenes.length - 1}
                                              style={{color: index == props.section.scenes.length - 1 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              type={'text'}
                                              icon={<FontAwesomeIcon icon={faArrowDown}/>} onClick={() => {
                                          let scenes = [...props.section.scenes]
                                          let next = {...scenes[index + 1]}
                                          let current = {...scenes[index]}
                                          let nextId = next.id
                                          next.id = current.id
                                          current.id = nextId

                                          dispatch(updateSection({
                                              // @ts-ignore
                                              section: {
                                                  ...props.section,
                                                  scenes: [...scenes.filter((item) => item.id !== next.id && item.id !== current.id)].concat([current, next]).sort((a, b) => a.id - b.id) as []
                                              }
                                          }))
                                      }}/>
                                      <Button icon={<FontAwesomeIcon icon={faTrash}/>} onClick={() => {

                                          dispatch(updateSection({
                                              // @ts-ignore
                                              section: {
                                                  ...props.section,
                                                  scenes: props.section.scenes.filter((it) => it.id !== item.id) as []
                                              }
                                          }))
                                      }}/>
                                      <GenButton
                                          mode={'icon'}
                                          hideMainAction={true}
                                          icon={<IconBoltGrad/>}
                                          actions={[{
                                              key: "1",
                                              title: "Re-generate",
                                              action: async (model: string, projectId: string) => generateScene(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faRotate} size={16}/>{title}
                                                  </Space>
                                              }
                                          }, {
                                              key: "2",
                                              title: "Improve",
                                              action: async (model: string, projectId: string) => improveScene(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faArrowUpWideShort}
                                                                          size={16}/>{title}</Space>
                                              }
                                          }]}/>
                                  </Space>
                              </Flex>
                          </Flex>}>

                        <TextAreaActions
                            style={{border: "none"}}
                            value={item.description}
                            autoSize={true}
                            validate={text => true}
                            onChange={(text: string) => {
                                dispatch(updateScene({
                                    // @ts-ignore
                                    scene: {
                                        ...item,
                                        description: text
                                    }
                                }))
                            }}
                        />
                    </Card>
                )}
            </Space>
        </Flex>
    )
}